/**
 * AgentHQ navigation framework (Phase 2 — structure only).
 * Approved V1 navigation. Routes render protected placeholders; no modules built.
 */
import {
  LayoutDashboard,
  Calendar,
  ListChecks,
  FileText,
  Users,
  KanbanSquare,
  FileSignature,
  Wallet,
  Megaphone,
  CalendarClock,
  Gem,
  ShieldCheck,
  GraduationCap,
  Award,
  BarChart3,
  Target,
  LifeBuoy,
  Settings,
  UserPlus,
  Flame,
  CalendarPlus,
  Upload,
  Send,
  CheckSquare,
} from "lucide-react";

// Grouped sidebar sections (exact approved V1 structure).
export const NAV_SECTIONS = [
  {
    id: "home",
    label: "Home",
    items: [{ key: "dashboard", label: "Dashboard", path: "/", icon: LayoutDashboard }],
  },
  {
    id: "run",
    label: "Run",
    items: [
      { key: "calendar", label: "Calendar", path: "/calendar", icon: Calendar },
      { key: "tasks", label: "Tasks", path: "/tasks", icon: ListChecks },
      { key: "documents", label: "Documents", path: "/documents", icon: FileText },
    ],
  },
  {
    id: "sell",
    label: "Sell",
    items: [
      { key: "crm", label: "CRM", path: "/crm", icon: Users },
      { key: "pipeline", label: "Pipeline", path: "/pipeline", icon: KanbanSquare },
      { key: "transactions", label: "Transactions", path: "/transactions", icon: FileSignature },
      { key: "commissions", label: "Commissions", path: "/commissions", icon: Wallet },
    ],
  },
  {
    id: "market",
    label: "Market",
    items: [
      { key: "marketing", label: "Marketing Center", path: "/marketing", icon: Megaphone },
      { key: "content-calendar", label: "Content Calendar", path: "/content-calendar", icon: CalendarClock },
      { key: "brand-profile", label: "Brand Profile", path: "/brand-profile", icon: Gem },
    ],
  },
  {
    id: "protect",
    label: "Protect",
    items: [
      { key: "compliance", label: "License & Compliance", path: "/compliance", icon: ShieldCheck },
      { key: "education", label: "Education", path: "/education", icon: GraduationCap },
      { key: "memberships", label: "Memberships", path: "/memberships", icon: Award },
    ],
  },
  {
    id: "grow",
    label: "Grow",
    items: [
      { key: "analytics", label: "Analytics", path: "/analytics", icon: BarChart3 },
      { key: "goals", label: "Goals", path: "/goals", icon: Target },
    ],
  },
];

export const BOTTOM_ITEMS = [
  { key: "help", label: "Help & Support", path: "/help", icon: LifeBuoy },
  { key: "settings", label: "Settings", path: "/settings", icon: Settings },
];

// Flat lookup (title/section resolution) including bottom items.
export const ALL_NAV_ITEMS = [
  ...NAV_SECTIONS.flatMap((s) => s.items.map((i) => ({ ...i, section: s.label }))),
  ...BOTTOM_ITEMS.map((i) => ({ ...i, section: "Support" })),
];

export function getRouteMeta(pathname) {
  const match = ALL_NAV_ITEMS.find((i) => i.path === pathname);
  if (match) return { title: match.label, section: match.section };
  if (pathname.startsWith("/admin")) return { title: "Admin", section: "Platform" };
  if (pathname === "/foundation") return { title: "Design Foundation", section: "Internal" };
  return { title: "AgentHQ", section: null };
}

// Global + Create menu (future modules — disabled in Phase 2).
export const QUICK_CREATE = [
  { key: "contact", label: "New Contact", icon: UserPlus },
  { key: "lead", label: "New Lead", icon: Flame },
  { key: "transaction", label: "New Transaction", icon: FileSignature },
  { key: "task", label: "New Task", icon: CheckSquare },
  { key: "event", label: "New Calendar Event", icon: CalendarPlus },
  { key: "document", label: "Upload Document", icon: Upload },
  { key: "goal", label: "New Goal", icon: Target },
  { key: "license", label: "Add License", icon: ShieldCheck },
  { key: "post", label: "Create Social Post", icon: Send },
];

// Future global-search scopes (boundary only — no results yet).
export const SEARCH_SCOPES = ["Contacts", "Leads", "Transactions", "Documents", "Tasks", "Notes"];

// Mobile bottom navigation (legitimate placeholder routes + actions).
export const MOBILE_NAV = [
  { key: "home", label: "Home", path: "/", icon: LayoutDashboard },
  { key: "crm", label: "CRM", path: "/crm", icon: Users },
  { key: "create", label: "Create", action: "create", icon: UserPlus },
  { key: "tasks", label: "Tasks", path: "/tasks", icon: ListChecks },
  { key: "more", label: "More", action: "more", icon: null },
];
